import { Button, Flex, IllustratedMessage, Progress, Stack } from '@eleveo/components-react';
import { SearchNoneIcon } from '@eleveo/icons-react';
import { pathOr } from 'ramda';
import React, { useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { RouteComponentProps } from 'react-router';

import { ScheduleElementsHeaderFilterType, ScheduleElementsHeaderSearchForm } from './ScheduleElementsHeaderSearchForm';
import { ScheduleElementsSidebar } from './ScheduleElementsSidebar';
import { ScheduleElementsTable } from './ScheduleElementsTable';
import { PARAMETERS_ERRORS_KEYS_ENDING_PARTS } from './config';
import { BUTTON_TOGGLE_SCHEDULE_ELEMENT_DETAIL_SIDEBAR, CONTAINER_TAG, TITLE_TAG } from './constants';
import { ScheduleElementValues } from './types';
import { transformFormValuesToScheduleElementObject } from './utils';
import { Permissions, useAuth } from '../../app/AuthorizationProvider';
import { Header } from '../../components/LayoutNext/Header';
import { Content, Panel, SidebarWrapper } from '../../components/LayoutNext/LayoutNext';
import { Center } from '../../components/LayoutNext/components/Center';
import { ModalConfirmationNewStyle as ModalConfirmation } from '../../components/ModalConfirmationNewStyle';
import { translateScheduleElementName } from '../../components/MySchedule/transformData';
import { useSidebar } from '../../components/layout/Sidebar';
import {
  GetScheduleElementsDocument,
  ScheduleElement,
  useCreateScheduleElementMutation,
  useDeleteScheduleElementMutation,
  useGetScheduleElementsQuery,
  useUpdateDisplayOrderOnScheduleElementMutation,
  useUpdateScheduleElementMutation,
} from '../../graphql/components';
import { useFilter } from '../../utils/filter';

import type { ActionRouteType } from '../../types';

export const ScheduleElementsScene: React.FC<RouteComponentProps<ActionRouteType>> = () => {
  const { t } = useTranslation();
  const { openSidebar, closeSidebar, isSidebarOpen } = useSidebar();
  const { userHasPermissions } = useAuth();

  const { filter, setFilter, isFilterActive } = useFilter<ScheduleElementsHeaderFilterType>();

  const [scheduleElementToDelete, setScheduleElementToDelete] = useState<ScheduleElement | null>(null);
  const [scheduleElementToEdit, setScheduleElementToEdit] = useState<ScheduleElement | null>(null);

  const scheduleElementsResult = useGetScheduleElementsQuery({ fetchPolicy: 'network-only' });
  const [updateDisplayOrderOnScheduleElement, { loading: isPendingUpdateDisplayOrderOnScheduleElement }] =
    useUpdateDisplayOrderOnScheduleElementMutation();
  const [editScheduleElement, { loading: isPendingEditScheduleElement }] = useUpdateScheduleElementMutation();
  const [createScheduleElement, { loading: isPendingCreateScheduleMutation }] = useCreateScheduleElementMutation();
  const [deleteScheduleElement, { loading: isPendingDeleteScheduleElement }] = useDeleteScheduleElementMutation();

  const mutationInProgress =
    isPendingUpdateDisplayOrderOnScheduleElement ||
    isPendingCreateScheduleMutation ||
    isPendingDeleteScheduleElement ||
    isPendingEditScheduleElement;

  const filteredLocalizedScheduleElements: ScheduleElement[] = useMemo(() => {
    const scheduleElements = scheduleElementsResult.data?.scheduleElements ?? [];

    const localizedScheduleElements = scheduleElements.map((scheduleElement) => ({
      ...scheduleElement,
      localizedName: translateScheduleElementName(t, scheduleElement),
    }));

    const searchValue = filter.name?.toLowerCase();

    if (!searchValue) {
      return localizedScheduleElements;
    }

    return localizedScheduleElements.filter((localizedScheduleElement) =>
      localizedScheduleElement.localizedName.toLowerCase().includes(searchValue)
    );
  }, [scheduleElementsResult, t, filter.name]);

  const onFilterSubmit = ({ name }: ScheduleElementsHeaderFilterType) => {
    const values = {
      name: name && name.trim(),
    };
    setFilter(values);
  };

  const handleDisplayOrderMove = async (scheduleElementId: string, newDisplayOrderPosition: number) => {
    try {
      await updateDisplayOrderOnScheduleElement({
        variables: { id: scheduleElementId, scheduleElementDisplayOrder: { displayOrder: newDisplayOrderPosition } },
        awaitRefetchQueries: true,
        refetchQueries: [GetScheduleElementsDocument],
      });
    } catch (e) {
      // `customFetch()` will display error message
    }
  };

  const handleShowDeleteModal = (scheduleElement: ScheduleElement) => setScheduleElementToDelete(scheduleElement);
  const handleCloseDeleteModal = () => setScheduleElementToDelete(null);

  const handleDelete = async () => {
    if (!scheduleElementToDelete) {
      return;
    }

    try {
      await deleteScheduleElement({
        variables: { id: scheduleElementToDelete.id },
        awaitRefetchQueries: true,
        refetchQueries: [GetScheduleElementsDocument],
      });
    } catch (e) {
      // `customFetch()` will display error message
    } finally {
      handleCloseDeleteModal();
    }
  };

  const { error, loading } = scheduleElementsResult;
  const hasResults = filteredLocalizedScheduleElements.length > 0;
  const showNoResultsNote = isFilterActive && !hasResults && !loading && !error;

  const closeSidebarAndResetEditState = () => {
    closeSidebar();
    setScheduleElementToEdit(null);
  };

  const toggleCreateElementSidebar = () => {
    if (!isSidebarOpen) {
      setScheduleElementToEdit(null);
      openSidebar();
    } else if (scheduleElementToEdit) {
      setScheduleElementToEdit(null);
    } else {
      closeSidebarAndResetEditState();
    }
  };

  const handleCreate = async (values: ScheduleElementValues) => {
    const elementToCreate = transformFormValuesToScheduleElementObject(values);

    try {
      await createScheduleElement({
        variables: { scheduleElement: elementToCreate },
        refetchQueries: [GetScheduleElementsDocument],
      });
      closeSidebar();
    } catch (e) {
      // `customFetch()` will display error message
      const errorCode = pathOr(null, ['networkError', 'result', 'errorCode'], e) as string;

      if (errorCode === 'fe.sch.scheduleelement.notUniqueName') {
        return {
          name: t(`response.${errorCode}`), // marks "name" field as invalid
        };
      }

      const errorCodeParts = errorCode.split('.');
      const errorCodeEndingPart = errorCodeParts[errorCodeParts.length - 1];

      if (
        errorCode.startsWith('fe.sch.scheduleelement') &&
        PARAMETERS_ERRORS_KEYS_ENDING_PARTS.includes(errorCodeEndingPart)
      ) {
        return {
          parameters: t(`response.${errorCode}`), // marks "parameters" field as invalid
        };
      }
    }

    return undefined;
  };

  const handleEdit = async (values: ScheduleElementValues) => {
    const elementToEdit = transformFormValuesToScheduleElementObject(values);

    try {
      await editScheduleElement({
        variables: { id: (scheduleElementToEdit as ScheduleElement).id, scheduleElement: elementToEdit },
        refetchQueries: [GetScheduleElementsDocument],
      });

      closeSidebarAndResetEditState();
    } catch (e) {
      // `customFetch()` will display error message
      const errorCode = pathOr(null, ['networkError', 'result', 'errorCode'], e) as string;

      if (errorCode === 'fe.sch.scheduleelement.notUniqueName') {
        return {
          name: t(`response.${errorCode}`), // marks "name" field as invalid
        };
      }

      const errorCodeParts = errorCode.split('.');
      const errorCodeEndingPart = errorCodeParts[errorCodeParts.length - 1];

      if (
        errorCode.startsWith('fe.sch.scheduleelement') &&
        PARAMETERS_ERRORS_KEYS_ENDING_PARTS.includes(errorCodeEndingPart)
      ) {
        return {
          parameters: t(`response.${errorCode}`), // marks "parameters" field as invalid
        };
      }
    }

    return undefined;
  };

  const prepareScheduleElementToEdit = (scheduleElement: ScheduleElement) => {
    setScheduleElementToEdit(scheduleElement);

    if (!isSidebarOpen) {
      openSidebar();
    }
  };

  const hasEditPermission = userHasPermissions([Permissions.EditScheduleElements]);

  return (
    <>
      <Header title={t('scheduleElements.scheduleElement', { count: 2 })} testId={TITLE_TAG} />

      <Panel>
        <Flex alignContent="between">
          <Stack direction="horizontal" spacing={15} className="w-full">
            <ScheduleElementsHeaderSearchForm onSubmit={onFilterSubmit} />
          </Stack>

          {hasEditPermission && (
            <Button
              isSelected={isSidebarOpen && !scheduleElementToEdit}
              onClick={toggleCreateElementSidebar}
              testId={BUTTON_TOGGLE_SCHEDULE_ELEMENT_DETAIL_SIDEBAR}
            >
              {t('form.addNew')}
            </Button>
          )}
        </Flex>
      </Panel>

      <Content testId={CONTAINER_TAG}>
        {(loading || mutationInProgress) && <Progress withOverlay />}

        {!loading && !error && hasResults && (
          <ScheduleElementsTable
            activeId={scheduleElementToDelete?.id}
            onDeleteScheduleElement={handleShowDeleteModal}
            onEditScheduleElement={prepareScheduleElementToEdit}
            onDisplayOrderMove={handleDisplayOrderMove}
            scheduleElements={filteredLocalizedScheduleElements}
          />
        )}

        {showNoResultsNote && (
          <Center>
            <IllustratedMessage heading={t('filter.noResultsMatch')} icon={<SearchNoneIcon />} />
          </Center>
        )}

        <ModalConfirmation
          acceptCaption={t('layout.action.delete')}
          cancelCaption={t('layout.action.cancel')}
          isOpen={!!scheduleElementToDelete}
          onAccept={handleDelete}
          onCancel={handleCloseDeleteModal}
          title={t('scheduleElements.deleteScheduleElement')}
        >
          <Trans
            i18nKey="scheduleElements.modal.deleteScheduleElementBody"
            values={{
              name: scheduleElementToDelete ? translateScheduleElementName(t, scheduleElementToDelete) : undefined,
            }}
          >
            Do you really want to delete chosen schedule element
            <em>{scheduleElementToDelete ? translateScheduleElementName(t, scheduleElementToDelete) : undefined}</em> ?
          </Trans>
        </ModalConfirmation>
      </Content>

      <SidebarWrapper>
        {isSidebarOpen && (
          <ScheduleElementsSidebar
            handleCancel={closeSidebarAndResetEditState}
            handleSubmit={scheduleElementToEdit ? handleEdit : handleCreate}
            scheduleElement={scheduleElementToEdit || undefined}
          />
        )}
      </SidebarWrapper>
    </>
  );
};
